import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HelpIcon from '@mui/icons-material/Help';
import ImageIcon from '@mui/icons-material/Image';
import SettingsIcon from '@mui/icons-material/Settings';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import VideocamIcon from '@mui/icons-material/Videocam';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TuneIcon from '@mui/icons-material/Tune';
import ScienceIcon from '@mui/icons-material/Science';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getAdminRole, getAdminStoreCode } from '../utils/auth';
import mogooLogo from '../owner-console-v2/assets-mogoo-logo.png';

export const DRAWER_WIDTH = 240;

/**
 * role 기반 메뉴 정의
 * masterOnly: true → master만 볼 수 있음 (매니저에게 숨김)
 * managerOnly: true → manager만 볼 수 있음 (master/staff에게 숨김)
 * modernwashOnly: true → master 또는 ModernWash 연동 매장(jeonju) 매니저만. 그 외(타매장 매니저·staff)에게 숨김.
 */
// ModernWash 미러/카메라 메뉴를 노출할 연동 매장 code (백엔드 MODERNWASH_STORE_CODE 와 일치).
const MODERNWASH_STORE_CODE = 'jeonju';

const menuItems = [
  { label: '대시보드',    path: '/',              icon: <DashboardIcon /> },
  // ModernWash 운영 = 레거시 V1 미러 전용 화면. master + 전주점(jeonju) 매니저만 노출(데이터는 백엔드 스코프로도 강제).
  { label: 'ModernWash 운영', path: '/modernwash', icon: <StorefrontIcon />, modernwashOnly: true },
  { label: '일일 세차 현황', path: '/modernwash/daily', icon: <LocalCarWashIcon />, modernwashOnly: true },
  { label: '일별 세차 현황', path: '/modernwash/range', icon: <AssignmentIcon />, modernwashOnly: true },
  // 외주(gtli) 관리자 파리티 — 차량 관리·세차 차량 기록·세차요금 기록을 그대로 재현(live read-only, master+jeonju만).
  { label: '차량 관리(V1)', path: '/modernwash/vehicle-list', icon: <DirectionsCarIcon />, modernwashOnly: true },
  { label: '세차 차량 기록', path: '/modernwash/wash-log', icon: <LocalCarWashIcon />, modernwashOnly: true },
  { label: '세차요금 기록', path: '/modernwash/payment-list', icon: <ReceiptLongIcon />, modernwashOnly: true },
  // 매장 정보/신청 승인은 v1에서 제거 — 매장 관련 운영은 점주 콘솔 v2로 일원화
  // 공지/FAQ/배너 — master는 글로벌 작성, manager는 본인 매장만 작성 (백엔드가 자동 스코프)
  { label: '공지사항',    path: '/notices',        icon: <NotificationsIcon /> },
  { label: 'FAQ',        path: '/faqs',           icon: <HelpIcon />,         masterOnly: true },
  { label: '배너 관리',   path: '/banners',        icon: <ImageIcon /> },
  { label: '사용자 관리', path: '/members',        icon: <PeopleIcon /> },
  { label: '구독 관리',   path: '/subscriptions',  icon: <SubscriptionsIcon /> },
  { label: '판매정책',    path: '/settings',       icon: <SettingsIcon />,     masterOnly: true },
  { label: '앱 버전 관리', path: '/versions',      icon: <PhoneAndroidIcon />, masterOnly: true },
  { label: '매출 관리',   path: '/sales',          icon: <PaymentIcon />,       masterOnly: true },
  { label: '이용 관리',   path: '/usage',          icon: <LocalCarWashIcon />,  masterOnly: true },
  { label: '카메라',      path: '/camera',         icon: <VideocamIcon />, modernwashOnly: true },
  { label: 'LPR 테스트',  path: '/lpr-test',       icon: <ScienceIcon />,      masterOnly: true },
  { label: '인식 로그',   path: '/lpr-logs',       icon: <AssignmentIcon /> },
  { label: '키오스크',    path: '/kiosk',          icon: <TuneIcon />,         masterOnly: true },
  { label: '계정 관리',   path: '/admins',         icon: <PeopleIcon />,       masterOnly: true },
  // 점주 콘솔 v2는 별도 제품 경로로 유지하지만 ModernWash v1 메뉴에서는 노출하지 않는다.
];

/**
 * 반응형 사이드바
 * - 데스크톱(md+): permanent Drawer
 * - 모바일(md 미만): temporary Drawer + 외부 토글
 *
 * props:
 *   mobileOpen: 모바일 drawer 열림 상태 (MainLayout에서 관리)
 *   onMobileClose: drawer 닫기 콜백
 */
const Sidebar = ({ mobileOpen = false, onMobileClose = () => {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // 1200px(lg) 미만에서 햄버거 메뉴로 전환
  // 이유: 사이드바 240px + 본문 padding이 차지하면 1024px 디스플레이도 본문이 좁아짐
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  // role 은 JWT 에서 읽는다 (localStorage 직접 읽기는 사용자가 콘솔에서 변조 가능)
  const role = getAdminRole();                          // 'master' | 'manager' | null
  const adminName = localStorage.getItem('adminName');
  const isMaster = role === 'master';
  const storeCode = getAdminStoreCode();                // manager 의 매장 code (JWT)

  // master만 볼 수 있는 메뉴는 매니저에게 숨김.
  // ModernWash(미러/카메라) 메뉴는 master 또는 연동매장(jeonju) 매니저만 — 그 외에는 숨김.
  const visibleMenus = menuItems.filter(
    (item) =>
      (!item.masterOnly || isMaster) &&
      (!item.managerOnly || role === 'manager') &&
      (!item.modernwashOnly ||
        isMaster ||
        (role === 'manager' && storeCode === MODERNWASH_STORE_CODE))
  );

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) onMobileClose();  // 모바일에서 메뉴 선택 시 자동 닫기
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 브랜드 / 사용자 헤더 */}
      <Box sx={{ p: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              borderRadius: '14px',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(220,235,255,0.92))',
              boxShadow: '0 8px 18px rgba(5, 15, 45, 0.22)',
            }}
          >
            <Box component="img" src={mogooLogo} alt="" sx={{ width: 33, height: 34, objectFit: 'contain' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#fff', fontSize: 18, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15 }}>
              ModernWash
            </Typography>
            <Typography sx={{ color: 'rgba(210,223,255,0.62)', fontSize: 10, fontWeight: 700, letterSpacing: '0.09em', mt: 0.35 }}>
              OPERATIONS
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 1.75,
            px: 1.25,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            backgroundColor: 'rgba(255,255,255,0.045)',
          }}
        >
          <Typography noWrap sx={{ minWidth: 0, color: 'rgba(255,255,255,0.82)', fontSize: 12, fontWeight: 600 }}>
            {adminName || '관리자'}
          </Typography>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              px: 0.85,
              py: 0.35,
              borderRadius: '999px',
              backgroundColor: isMaster ? 'rgba(236, 72, 153, 0.18)' : 'rgba(96, 165, 250, 0.16)',
              border: isMaster ? '1px solid rgba(244, 114, 182, 0.4)' : '1px solid rgba(147, 197, 253, 0.35)',
              color: isMaster ? '#F9A8D4' : '#BFDBFE',
              fontSize: 9,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: '0.05em',
            }}
          >
            {isMaster ? 'MASTER' : 'MANAGER'}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

      <List sx={{ px: 1.5, mt: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        {visibleMenus.map((item) => {
          const isSelected = !item.external && (item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path));
          return (
            <ListItemButton
              key={item.path}
              selected={isSelected}
              disabled={item.disabled}
              onClick={() => handleNavigate(item.path)}
              sx={{
                borderRadius: '8px',
                mb: 0.5,
                py: 1,
                color: 'rgba(255,255,255,0.65)',
                position: 'relative',
                transition: 'all 0.2s ease',
                // 선택된 항목: 핑크 강조 + 좌측 인디케이터
                '&.Mui-selected': {
                  background: 'linear-gradient(135deg, rgba(233,30,99,0.18) 0%, rgba(233,30,99,0.08) 100%)',
                  color: '#fff',
                  '& .MuiListItemIcon-root': { color: '#E91E63' },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '20%',
                    bottom: '20%',
                    width: 3,
                    borderRadius: '0 3px 3px 0',
                    backgroundColor: '#E91E63',
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(233,30,99,0.22) 0%, rgba(233,30,99,0.10) 100%)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  color: 'rgba(255,255,255,0.9)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36, transition: 'color 0.2s ease' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: { sx: { fontSize: 14, fontWeight: isSelected ? 700 : 500 } },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ mt: 'auto', p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('adminRole');
            localStorage.removeItem('adminName');
            localStorage.removeItem('adminStoreId');
            localStorage.removeItem('adminStoreCode');
            navigate('/login');
            if (isMobile) onMobileClose();
          }}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            borderColor: 'rgba(255,255,255,0.2)',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.5)',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
          }}
        >
          로그아웃
        </Button>
      </Box>
    </Box>
  );

  const drawerSx = {
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      // 그라데이션 배경 — AppBar와 일관성
      background: 'linear-gradient(180deg, #1a1a2e 0%, #16162b 50%, #1a1a3e 100%)',
      color: '#fff',
      borderRight: '1px solid rgba(255,255,255,0.05)',
    },
  };

  // 데스크톱: permanent drawer
  if (!isMobile) {
    return (
      <Drawer
        variant="permanent"
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, ...drawerSx }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // 모바일: temporary drawer (햄버거 클릭 시 열림)
  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}  // 모바일 성능 개선
      sx={drawerSx}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
